import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DialogProvider } from './contexts/DialogContext';
import { ErrorBoundary, ErrorProvider } from './components/ErrorBoundary';
import './i18n/config'; // Initialize i18n

// Sentry error monitoring (only if DSN configured)
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    integrations: [],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      // Allowlist error diagnostics; exclude requests, breadcrumbs, user and arbitrary context.
      const safe = {};
      for (const key of ['event_id', 'timestamp', 'platform', 'level', 'release', 'environment']) {
        if (event[key] !== undefined) safe[key] = event[key];
      }
      if (event.exception?.values) {
        safe.exception = { values: event.exception.values.map(error => ({
          type: error.type,
          value: '[Details removed for privacy]',
          stacktrace: { frames: (error.stacktrace?.frames || []).map(frame => ({
            filename: String(frame.filename || '').split(/[?#]/)[0].split('/').pop(),
            function: frame.function,
            lineno: frame.lineno,
          })) },
        })) };
      }
      return safe;
    },
  });
}

// Register PWA Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <ErrorProvider>
          <AuthProvider>
            <DialogProvider>
              <App />
            </DialogProvider>
          </AuthProvider>
        </ErrorProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
